import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface EventFile {
  id: string;
  event_type: 'interaction' | 'follow-up';
  event_id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size?: number;
  thumbnail_url?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
  storage_path: string; // Path in storage for deletion
}

/**
 * Fetch files for a specific event from Storage bucket
 */
export function useEventFiles(eventType: 'interaction' | 'follow-up', eventId: string | null) {
  return useQuery({
    queryKey: ['event-files', eventType, eventId],
    queryFn: async () => {
      if (!eventId) return [];
      
      const folder = `${eventType}/${eventId}`;
      
      // List files from Storage bucket
      const { data, error } = await supabase.storage
        .from('crm-event-files')
        .list(folder, {
          sortBy: { column: 'created_at', order: 'desc' }
        });
      
      if (error) throw error;
      
      // Convert storage objects to EventFile format
      return (data || [])
        .filter(file => file.name !== '.emptyFolderPlaceholder')
        .map(file => {
          const filePath = `${folder}/${file.name}`;
          const { data: urlData } = supabase.storage
            .from('crm-event-files')
            .getPublicUrl(filePath);
          
          // Extract file extension and determine MIME type
          const extension = file.name.split('.').pop()?.toLowerCase();
          const mimeType = file.metadata?.mimetype || 
            (extension === 'jpg' || extension === 'jpeg' ? 'image/jpeg' :
             extension === 'png' ? 'image/png' :
             extension === 'pdf' ? 'application/pdf' :
             'application/octet-stream');
          
          return {
            id: file.id || file.name,
            event_type: eventType,
            event_id: eventId,
            file_name: file.name,
            file_url: urlData.publicUrl,
            file_type: mimeType,
            file_size: file.metadata?.size,
            thumbnail_url: mimeType.startsWith('image/') ? urlData.publicUrl : undefined,
            created_at: file.created_at || new Date().toISOString(),
            updated_at: file.updated_at || new Date().toISOString(),
            storage_path: filePath
          } as EventFile;
        });
    },
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Upload file for an event directly to Storage
 */
export function useUploadEventFile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: {
      eventType: 'interaction' | 'follow-up';
      eventId: string;
      file: File;
    }) => {
      const { eventType, eventId, file } = params;
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${eventType}/${eventId}/${timestamp}-${sanitizedFileName}`;
      
      // Upload directly to Storage bucket
      const { data, error } = await supabase.storage
        .from('crm-event-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) throw error;
      
      const publicUrl = supabase.storage
        .from('crm-event-files')
        .getPublicUrl(data.path).data.publicUrl;
      
      return {
        id: data.path,
        event_type: eventType,
        event_id: eventId,
        file_name: file.name,
        file_url: publicUrl,
        file_type: file.type,
        file_size: file.size,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        storage_path: data.path
      } as EventFile;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['event-files', variables.eventType, variables.eventId] 
      });
      toast.success('File uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });
}

/**
 * Delete event file from Storage
 */
export function useDeleteEventFile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: {
      storagePath: string;
      eventType: 'interaction' | 'follow-up';
      eventId: string;
    }) => {
      const { storagePath, eventType, eventId } = params;
      
      // Delete from Storage bucket
      const { error } = await supabase.storage
        .from('crm-event-files')
        .remove([storagePath]);
      
      if (error) throw error;
      
      return { eventType, eventId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ 
        queryKey: ['event-files', result.eventType, result.eventId] 
      });
      toast.success('File deleted');
    },
    onError: (error: Error) => {
      toast.error(`Delete failed: ${error.message}`);
    },
  });
}
